import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { teamAPI, graphicAPI, rosterAPI } from '../services/api';
import html2canvas from 'html2canvas';

const toBase64 = (urlOrBlob) => {
  if (typeof urlOrBlob === 'string' && urlOrBlob.startsWith('data:')) {
    return Promise.resolve(urlOrBlob);
  }
  return new Promise((resolve, reject) => {
    fetch(urlOrBlob)
      .then((r) => r.blob())
      .then((blob) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
  });
};

export function useGraphicForm() {
  const navigate = useNavigate();

  // All your state
  const [teams, setTeams] = useState([]);
  const [downloading, setDownloading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [templateType, setTemplateType] = useState('preview');
  const [template, setTemplate] = useState('classic');
  const [currentGraphicId, setCurrentGraphicId] = useState(null);
  const [saveMessage, setSaveMessage] = useState('');
  const [savingOverlay, setSavingOverlay] = useState('');
  const [backgroundStyle, setBackgroundStyle] = useState('linear-gradient');
  const [graphicsLimitInfo, setGraphicsLimitInfo] = useState(null);
  const [homeTeamLogo, setHomeTeamLogo] = useState(null);
  // eslint-disable-next-line
  const [awayTeamLogoFile, setAwayTeamLogoFile] = useState(null);
  const [awayTeamLogoPreview, setAwayTeamLogoPreview] = useState(null);
  // eslint-disable-next-line
  const [backgroundPhoto, setBackgroundPhoto] = useState(null);
  const [backgroundPhotoPreview, setBackgroundPhotoPreview] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [toastMessage, setToastMessage] = useState({ type: '', text: '' });
  const [roster, setRoster] = useState([]);
  const [showRosterDropdown, setShowRosterDropdown] = useState(false);
  const [filteredRoster, setFilteredRoster] = useState([]);
  // eslint-disable-next-line
  const [playerPhoto, setPlayerPhoto] = useState(null);
  const [originalPlayerPhoto, setOriginalPlayerPhoto] = useState(null);
  const [cutoutPlayerPhoto, setCutoutPlayerPhoto] = useState(null);
  const [activePlayerPhotoVersion, setActivePlayerPhotoVersion] = useState('original');
  const [originalDominantImage, setOriginalDominantImage] = useState(null);
  const [cutoutDominantImage, setCutoutDominantImage] = useState(null);
  const [activeDominantImageVersion, setActiveDominantImageVersion] = useState('original');
  const [dominantImageCropping, setDominantImageCropping] = useState(false);
  const [dominantImageTempImage, setDominantImageTempImage] = useState(null);
  const [dominantImageOriginalFile, setDominantImageOriginalFile] = useState(null);
  const [backgroundCropping, setBackgroundCropping] = useState(false);
  const [backgroundTempImage, setBackgroundTempImage] = useState(null);
  const [playerCropping, setPlayerCropping] = useState(false);
  const [playerTempImage, setPlayerTempImage] = useState(null);
  const [backgroundOriginalImage, setBackgroundOriginalImage] = useState(null);
  const [playerOriginalImage, setPlayerOriginalImage] = useState(null);
  const [formData, setFormData] = useState({
    selectedTeamId: '',
    homeTeam: '',
    awayTeam: '',
    gameDate: '',
    gameTime: '',
    location: '',
    homeScore: '',
    awayScore: '',
    topPerformer: '',
    topPerformerStats: '',
    ageGroup: '',
    tryoutDate: '',
    tryoutDate2: '',
    tryoutTime: '',
    whatToBring: '',
    cost: '',
    contact: '',
    additionalInfo: '',
    highlightPlayer: '',
    highlightStats: '',
    playerPhoto: null,
    dominantImage: null,
    heroFont: 'Bebas Neue',
    heroFontSize: 160,
    accentBlockColor: '',
    spotlightLabelFont: 'Barlow Condensed',
    spotlightLabelSize: 56,
    spotlightLabelColor: '#ffffff',
    spotlightLabelX: 540,
    dominantImageScale: 100,
    dominantImageX: 50,
    dominantImageY: 50,
    detailTextColor: '',
    bgImageScale: 100,
    bgImageX: 50,
    bgImageY: 50,
  });
  const [teamColors, setTeamColors] = useState({
    primary: '#E5E7EB',
    secondary: '#F9FAFB',
  });
  const [teamSport, setTeamSport] = useState('');
  const [isOrgTeam, setIsOrgTeam] = useState(false);
  const [orgTemplateSettings, setOrgTemplateSettings] = useState(null);
  const [showSwitchWarning, setShowSwitchWarning] = useState(false);
  const [pendingTeamSwitch, setPendingTeamSwitch] = useState(null);
  const [showLayoutModal, setShowLayoutModal] = useState(false);
  const [layoutSettings, setLayoutSettings] = useState({
    logoPosition: 'bottom-left',
    infoPosition: 'right',
    yearPosition: 'top-left',
    tryoutsOpacity: 1,
    textTreatment: 'shadow',
  });
  const [showDateFormatModal, setShowDateFormatModal] = useState(false);
  const [dateFormat, setDateFormat] = useState('full');

  const playerPhotoPreview =
    activePlayerPhotoVersion === 'cutout' && cutoutPlayerPhoto
      ? cutoutPlayerPhoto
      : originalPlayerPhoto;

  const dominantImagePreview =
    activeDominantImageVersion === 'cutout' && cutoutDominantImage
      ? cutoutDominantImage
      : originalDominantImage;

  // Helper functions
  const showToast = (type, text, duration = 3000) => {
    setToastMessage({ type, text });
    setTimeout(() => setToastMessage({ type: '', text: '' }), duration);
  };

  const canCreateMore = () => {
    if (!graphicsLimitInfo) return false;
    if (graphicsLimitInfo.unlimited) return true;
    const graphicsLimit = graphicsLimitInfo.graphicsLimit || Infinity;
    const currentCount = graphicsLimitInfo.graphicsCount || 0;
    return currentCount < graphicsLimit;
  };

  const checkTeamCompatibility = (newTeam) => {
    const currentTemplate = templateType;
    const currentDesign = template;

    if (
      !newTeam.organizationTeam &&
      graphicsLimitInfo?.subscriptionPlan === 'free'
    ) {
      if (currentTemplate === 'tryout' || currentTemplate === 'highlight')
        return false;
      if (currentDesign !== 'classic') return false;
    }

    if (newTeam.organizationTeam && newTeam.orgTemplateSettings) {
      if (
        currentTemplate === 'tryout' &&
        !newTeam.orgTemplateSettings.allowTryouts
      )
        return false;
      if (
        currentTemplate === 'highlight' &&
        !newTeam.orgTemplateSettings.allowPlayerHighlights
      )
        return false;
      if (
        currentDesign === 'splitScreen' &&
        !newTeam.orgTemplateSettings.allowSplitScreen
      )
        return false;
      if (currentDesign === 'bold' && !newTeam.orgTemplateSettings.allowBold)
        return false;
      if (
        currentDesign === 'minimal' &&
        !newTeam.orgTemplateSettings.allowMinimal
      )
        return false;
      if (
        currentDesign === 'photoFrame' &&
        !newTeam.orgTemplateSettings.allowPhotoFrame
      )
        return false;
    }

    return true;
  };

  const fetchRosterAsync = async (teamId) => {
    try {
      const response = await rosterAPI.getTeamRoster(teamId);
      setRoster(response.data);
      setFilteredRoster(response.data);
    } catch (err) {
      setRoster([]);
      setFilteredRoster([]);
    }
  };

  const performTeamSwitch = (selectedTeam, teamId) => {
    setFormData({
      ...formData,
      selectedTeamId: teamId,
      homeTeam: selectedTeam.name,
      backgroundPhoto: null,
      topPerformer: '',
    });
    setTeamColors({
      primary:
        selectedTeam.primaryColor || selectedTeam.orgPrimaryColor || '#003366',
      secondary:
        selectedTeam.secondaryColor ||
        selectedTeam.orgSecondaryColor ||
        '#FFFFFF',
    });
    setHomeTeamLogo(selectedTeam.logoUrl || selectedTeam.orgLogoUrl);
    setIsOrgTeam(selectedTeam.organizationTeam === true);
    setTeamSport(selectedTeam.sport);
    setOrgTemplateSettings(selectedTeam.orgTemplateSettings || null);
    fetchRosterAsync(teamId);
    setBackgroundPhoto(null);
    setBackgroundPhotoPreview(null);
  };

  // All your handlers
  const handleTeamChange = async (e) => {
    const teamId = e.target.value;
    try {
      const teamResponse = await teamAPI.getById(teamId);
      const selectedTeam = teamResponse.data;
      const isCompatible = checkTeamCompatibility(selectedTeam);

      if (!isCompatible) {
        setPendingTeamSwitch(selectedTeam);
        setShowSwitchWarning(true);
        return;
      }

      performTeamSwitch(selectedTeam, teamId);
    } catch (error) {
      console.error('Error fetching team details:', error);
      const selectedTeam = teams.find((t) => t.id === teamId);
      if (selectedTeam) {
        const isCompatible = checkTeamCompatibility(selectedTeam);
        if (!isCompatible) {
          setPendingTeamSwitch(selectedTeam);
          setShowSwitchWarning(true);
          return;
        }
        performTeamSwitch(selectedTeam, teamId);
      }
    }
  };

  const handleConfirmSwitch = () => {
    if (pendingTeamSwitch) {
      setTemplateType('preview');
      setTemplate('classic');
      setFormData({
        selectedTeamId: pendingTeamSwitch.id,
        homeTeam: pendingTeamSwitch.name,
        awayTeam: '',
        gameDate: '',
        gameTime: '',
        location: '',
        backgroundPhoto: null,
        topPerformer: '',
      });
      performTeamSwitch(pendingTeamSwitch, pendingTeamSwitch.id);
      setShowSwitchWarning(false);
      setPendingTeamSwitch(null);
    }
  };

  const handleCancelSwitch = () => {
    setFormData({ ...formData, selectedTeamId: formData.selectedTeamId });
    setShowSwitchWarning(false);
    setPendingTeamSwitch(null);
  };

  const handlePlayerPhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        showToast('error', 'Player photo must be less than 10MB');
        return;
      }
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
      if (!allowedTypes.includes(file.type)) {
        showToast('error', 'Player photo must be a JPEG or PNG image');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setPlayerOriginalImage(reader.result);
        setPlayerTempImage(reader.result);
        setPlayerCropping(true);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemovePlayerPhoto = () => {
    setPlayerPhoto(null);
    setOriginalPlayerPhoto(null);
    setCutoutPlayerPhoto(null);
    setActivePlayerPhotoVersion('original');
    setPlayerOriginalImage(null);
    setFormData({
      ...formData,
      playerPhoto: null,
      playerPhotoOriginal: null,
      playerPhotoCutout: null,
      playerPhotoVersion: 'original',
    });
  };

  const handleDominantImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        showToast('error', 'Image must be less than 10MB');
        return;
      }
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
      if (!allowedTypes.includes(file.type)) {
        showToast('error', 'Image must be a JPEG or PNG');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setDominantImageOriginalFile(reader.result);
        setDominantImageTempImage(reader.result);
        setDominantImageCropping(true);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveDominantImage = () => {
    setOriginalDominantImage(null);
    setCutoutDominantImage(null);
    setActiveDominantImageVersion('original');
    setDominantImageOriginalFile(null);
    setFormData({
      ...formData,
      dominantImage: null,
      dominantImageOriginal: null,
      dominantImageCutout: null,
      dominantImageVersion: 'original',
    });
  };

  const handleDominantImageCropComplete = async (croppedImage) => {
    const base64Image = await toBase64(croppedImage);
    setOriginalDominantImage(base64Image);
    setCutoutDominantImage(null);
    setActiveDominantImageVersion('original');
    setFormData({
      ...formData,
      dominantImage: base64Image,
      dominantImageOriginal: base64Image,
      dominantImageCutout: null,
      dominantImageVersion: 'original',
    });
    setDominantImageCropping(false);
    setDominantImageTempImage(null);
    const input = document.getElementById('dominant-image');
    if (input) input.value = '';
  };

  const handleDominantImageCropCancel = () => {
    setDominantImageCropping(false);
    setDominantImageTempImage(null);
    const input = document.getElementById('dominant-image');
    if (input) input.value = '';
  };

  const handleEditDominantImageCrop = () => {
    setDominantImageTempImage(dominantImageOriginalFile);
    setDominantImageCropping(true);
    setCutoutDominantImage(null);
    setActiveDominantImageVersion('original');
  };

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    const parsed = type === 'range' || type === 'number' ? Number(value) : value;
    setFormData({ ...formData, [name]: parsed });
  };

  const handleAdditionalInfoChange = (e) => {
    const value = e.target.value;
    const lineBreaks = (value.match(/\n/g) || []).length;
    if (lineBreaks > 2) return;
    setFormData({ ...formData, additionalInfo: value });
  };

  const handleHighlightStatsChange = (e) => {
    const value = e.target.value;
    const lineBreaks = (value.match(/\n/g) || []).length;
    if (lineBreaks > 2) return;
    setFormData({ ...formData, highlightStats: value });
  };

  const handleTemplateChange = (type) => {
    setTemplateType(type);
    if (type === 'tryout') setTemplate('poster');
  };

  const handleSave = async (showMessage = true) => {
    if (!formData.selectedTeamId) {
      showToast('error', 'Please select a team');
      return false;
    }

    if (templateType === 'tryout') {
      if (
        !formData.ageGroup ||
        !formData.tryoutDate ||
        !formData.tryoutTime ||
        !formData.location
      ) {
        showToast(
          'error',
          'Please fill in all required tryout fields (Age Group, Date, Time, Location)'
        );
        return false;
      }
    } else if (templateType === 'highlight') {
      if (!formData.highlightPlayer || !formData.highlightStats) {
        showToast('error', 'Please fill in player name and stats');
        return false;
      }
    } else {
      if (!formData.awayTeam) {
        showToast('error', 'Please enter opponent name');
        return false;
      }
    }

    setSaving(true);
    setSavingOverlay('Saving...');
    setSaveMessage('');
    setSuccessMessage('');

    try {
      const saveData = {
        id: currentGraphicId,
        teamId: formData.selectedTeamId,
        templateType,
        template,
        graphicData: {
          ...formData,
          awayTeamLogo: awayTeamLogoPreview,
          backgroundPhoto: backgroundPhotoPreview,
          dominantImage: (formData.dominantImageVersion === 'cutout' && formData.dominantImageCutout)
            ? formData.dominantImageCutout
            : formData.dominantImage,
          layoutSettings,
          dateFormat,
        },
      };

      const response = currentGraphicId
        ? await graphicAPI.update(currentGraphicId, saveData)
        : await graphicAPI.save(saveData);
      setCurrentGraphicId(response.data.id);

      if (showMessage) {
        setSaving(false);
        setSavingOverlay('');
        navigate(`/team/${formData.selectedTeamId}#${templateType}`);
      }

      return true;
    } catch (err) {
      if (
        err.response?.status === 403 &&
        err.response?.data?.error === 'Graphics limit reached'
      ) {
        showToast(
          'error',
          `${err.response.data.message}. Redirecting to upgrade options...`,
          3000
        );
        setTimeout(() => navigate('/membership'), 2000);
      } else {
        showToast('error', 'Failed to save graphic. Please try again.');
      }
      return false;
    } finally {
      setSaving(false);
      setSavingOverlay('');
    }
  };

  const handleSaveAndCreateAnother = async () => {
    if (!canCreateMore()) {
      const limit = graphicsLimitInfo?.graphicsLimit || 0;
      const plan = graphicsLimitInfo?.subscriptionPlan || 'free';
      showToast(
        'error',
        `You've reached your graphics limit (${limit} graphics) on the ${plan} plan. Redirecting...`,
        3000
      );
      setTimeout(() => navigate('/membership'), 2000);
      return;
    }

    const success = await handleSave(false);

    if (success) {
      const currentTeamId = formData.selectedTeamId;
      const currentTeamColors = { ...teamColors };
      const currentHomeTeamLogo = homeTeamLogo;

      setFormData({
        selectedTeamId: currentTeamId,
        homeTeam: formData.homeTeam,
        awayTeam: '',
        gameDate: '',
        gameTime: '',
        location: '',
        homeScore: '',
        awayScore: '',
        topPerformer: '',
        topPerformerStats: '',
        ageGroup: '',
        tryoutDate: '',
        tryoutDate2: '',
        tryoutTime: '',
        whatToBring: '',
        cost: '',
        contact: '',
        additionalInfo: '',
        highlightPlayer: '',
        highlightStats: '',
        playerPhoto: null,
        playerPhotoOriginal: null,
        playerPhotoCutout: null,
        playerPhotoVersion: 'original',
        dominantImage: null,
        dominantImageOriginal: null,
        dominantImageCutout: null,
        dominantImageVersion: 'original',
        heroFont: formData.heroFont || 'Bebas Neue',
        heroFontSize: formData.heroFontSize || 160,
        accentBlockColor: formData.accentBlockColor || '',
        spotlightLabelFont: formData.spotlightLabelFont || 'Barlow Condensed',
        spotlightLabelSize: formData.spotlightLabelSize || 56,
        spotlightLabelColor: formData.spotlightLabelColor || '#ffffff',
        spotlightLabelX: formData.spotlightLabelX ?? 540,
        dominantImageScale: 100,
        dominantImageX: 50,
        dominantImageY: 50,
        detailTextColor: '',
      });

      setPlayerPhoto(null);
      setOriginalPlayerPhoto(null);
      setCutoutPlayerPhoto(null);
      setActivePlayerPhotoVersion('original');
      setPlayerOriginalImage(null);
      setOriginalDominantImage(null);
      setCutoutDominantImage(null);
      setActiveDominantImageVersion('original');
      setDominantImageOriginalFile(null);
      setTeamColors(currentTeamColors);
      setHomeTeamLogo(currentHomeTeamLogo);
      setAwayTeamLogoFile(null);
      setAwayTeamLogoPreview(null);
      setBackgroundPhoto(null);
      setBackgroundPhotoPreview(null);
      setCurrentGraphicId(null);

      try {
        const response = await graphicAPI.getLimitInfo();
        setGraphicsLimitInfo(response.data);
      } catch (err) {}

      setSuccessMessage('✅ Graphic saved! Create another below.');
      setTimeout(() => setSuccessMessage(''), 3000);
      window.scrollTo(0, 0);
    }
  };

  const handleBackgroundStyleChange = (style) => {
    setBackgroundStyle(style);
  };

  const handleAwayTeamLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        showToast('error', 'Logo file must be less than 5MB');
        return;
      }
      const allowedTypes = [
        'image/jpeg',
        'image/jpg',
        'image/png',
        'image/gif',
        'image/svg+xml',
      ];
      if (!allowedTypes.includes(file.type)) {
        showToast(
          'error',
          'Logo must be an image file (JPEG, PNG, GIF, or SVG)'
        );
        return;
      }
      setAwayTeamLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setAwayTeamLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveAwayTeamLogo = () => {
    setAwayTeamLogoFile(null);
    setAwayTeamLogoPreview(null);
  };

  const handleBackgroundPhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        showToast('error', 'Background photo must be less than 10MB');
        return;
      }
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
      if (!allowedTypes.includes(file.type)) {
        showToast('error', 'Background photo must be a JPEG or PNG image');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setBackgroundOriginalImage(reader.result);
        setBackgroundTempImage(reader.result);
        setBackgroundCropping(true);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveBackgroundPhoto = () => {
    setBackgroundPhoto(null);
    setBackgroundPhotoPreview(null);
    setBackgroundOriginalImage(null);
    setFormData({ ...formData, backgroundPhoto: null });
  };

  const handleSetBackgroundPhotoFromUrl = (url) => {
    setBackgroundPhotoPreview(url);
    setFormData({ ...formData, backgroundPhoto: url });
  };

  const handleBackgroundCropComplete = (croppedImage) => {
    setBackgroundPhotoPreview(croppedImage);
    setFormData({ ...formData, backgroundPhoto: croppedImage });
    setBackgroundCropping(false);
    setBackgroundTempImage(null);
    const bgInput = document.getElementById('background-photo');
    if (bgInput) bgInput.value = '';
  };

  const handleBackgroundCropCancel = () => {
    setBackgroundCropping(false);
    setBackgroundTempImage(null);
    const bgInput = document.getElementById('background-photo');
    if (bgInput) bgInput.value = '';
  };

  const handlePlayerCropComplete = async (croppedImage) => {
    const base64Image = await toBase64(croppedImage);
    setOriginalPlayerPhoto(base64Image);
    setCutoutPlayerPhoto(null);
    setActivePlayerPhotoVersion('original');
    setFormData({
      ...formData,
      playerPhoto: base64Image,
      playerPhotoOriginal: base64Image,
      playerPhotoCutout: null,
      playerPhotoVersion: 'original',
    });
    setPlayerCropping(false);
    setPlayerTempImage(null);
    const playerInput = document.getElementById('player-photo');
    if (playerInput) playerInput.value = '';
  };

  const handlePlayerCropCancel = () => {
    setPlayerCropping(false);
    setPlayerTempImage(null);
    const playerInput = document.getElementById('player-photo');
    if (playerInput) playerInput.value = '';
  };

  const handleEditBackgroundCrop = () => {
    setBackgroundTempImage(backgroundOriginalImage);
    setBackgroundCropping(true);
  };

  const handleEditPlayerCrop = () => {
    setPlayerTempImage(playerOriginalImage);
    setPlayerCropping(true);
    setCutoutPlayerPhoto(null);
    setActivePlayerPhotoVersion('original');
  };

  const handlePlayerSearch = (e) => {
    const value = e.target.value;
    const fieldName = e.target.name;
    setFormData({ ...formData, [fieldName]: value });

    if (value.trim() === '') {
      setFilteredRoster(roster);
    } else {
      const filtered = roster.filter((player) => {
        const searchText = formatPlayerDisplay(player).toLowerCase();
        return searchText.includes(value.toLowerCase());
      });
      setFilteredRoster(filtered);
    }

    setShowRosterDropdown(true);
  };

  const handlePlayerSelect = (player) => {
    const displayText = formatPlayerDisplay(player);
    const fieldName =
      templateType === 'highlight' ? 'highlightPlayer' : 'topPerformer';
    setFormData({ ...formData, [fieldName]: displayText });
    setShowRosterDropdown(false);
  };

  const formatPlayerDisplay = (player) => {
    const isPremium = graphicsLimitInfo?.subscriptionPlan === 'premium';
    const parts = [];
    if (player.number) parts.push(`#${player.number}`);
    parts.push(player.name);
    if (isPremium && player.position) parts.push(`- ${player.position}`);
    return parts.join(' ');
  };

  const handleDownload = async () => {
    if (formData.selectedTeamId) {
      const saved = await handleSave(false);
      if (!saved) {
        setDownloading(false);
        setSavingOverlay('');
        return;
      }
    }

    setDownloading(true);
    setSavingOverlay('Downloading...');

    try {
      const elementId =
        templateType === 'preview'
          ? 'graphic-preview'
          : templateType === 'recap'
            ? 'graphic-recap'
            : templateType === 'tryout'
              ? 'graphic-tryout'
              : templateType === 'highlight'
                ? `graphic-player-spotlight-${template}`
                : 'graphic-highlight';

      const originalElement = document.getElementById(elementId);
      if (!originalElement) throw new Error('Graphic element not found');

      const tempContainer = document.createElement('div');
      tempContainer.style.position = 'fixed';
      tempContainer.style.left = '-9999px';
      tempContainer.style.top = '0';
      tempContainer.style.width = '1080px';
      tempContainer.style.zIndex = '-1';
      document.body.appendChild(tempContainer);

      const clonedElement = originalElement.cloneNode(true);
      clonedElement.style.width = '1080px';
      const isPortraitClone = (templateType === 'highlight' && ['showcase', 'feature'].includes(template)) || (templateType === 'tryout' && ['poster', 'infoCard'].includes(template));
      const isTryoutPortrait = templateType === 'tryout' && ['poster', 'infoCard'].includes(template);
      clonedElement.style.height = isPortraitClone && !isTryoutPortrait ? '1350px' : isTryoutPortrait ? 'auto' : '1080px';
      clonedElement.style.minHeight = isTryoutPortrait ? '1080px' : isPortraitClone ? '1350px' : '1080px';
      clonedElement.style.maxHeight = isTryoutPortrait ? '1350px' : isPortraitClone ? '1350px' : '1080px';
      clonedElement.style.overflow = 'hidden';
      clonedElement.style.transform = 'none';
      clonedElement.classList.add('download-mode');

      const backgroundImages = clonedElement.querySelectorAll(
        '.photo-frame-bg-img, .tryout-photo-frame-bg-img, .recap-photo-frame-bg-img, .photoframe-highlight-bg-img'
      );
      backgroundImages.forEach((img) => {
        const naturalWidth = img.naturalWidth;
        const naturalHeight = img.naturalHeight;
        const aspectRatio = naturalWidth / naturalHeight;
        const containerHeight = 1080;
        let newWidth = containerHeight * aspectRatio;
        let newHeight = containerHeight;
        if (newWidth > 1080) {
          newWidth = 1080;
          newHeight = 1080 / aspectRatio;
        }
        img.style.width = `${newWidth}px`;
        img.style.height = `${newHeight}px`;
        img.style.objectFit = 'contain';
        img.style.objectPosition = 'center bottom';
      });

      tempContainer.appendChild(clonedElement);
      await new Promise((resolve) => setTimeout(resolve, 500));

      const actualWidth = 1080;
      const isHighlightPortrait = templateType === 'highlight' && ['showcase', 'feature'].includes(template);
      const actualHeight = isHighlightPortrait ? Math.max(1350, clonedElement.offsetHeight) : isTryoutPortrait ? clonedElement.offsetHeight : 1080;

      const canvas = await html2canvas(clonedElement, {
        scale: 1,
        backgroundColor: null,
        logging: false,
        useCORS: true,
        allowTaint: true,
        width: actualWidth,
        height: actualHeight,
        imageTimeout: 0,
        removeContainer: true,
        windowWidth: 1080,
        windowHeight: actualHeight,
      });

      document.body.removeChild(tempContainer);

      canvas.toBlob(
        (blob) => {
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          const teamName = formData.homeTeam
            ? formData.homeTeam.replace(/\s+/g, '-')
            : 'graphic';
          const fileName = `${teamName}-${templateType}-${template}-${Date.now()}.png`;
          link.download = fileName;
          link.href = url;
          link.click();
          URL.revokeObjectURL(url);

          setDownloading(false);
          setSavingOverlay('');

          setTimeout(() => {
            navigate(`/team/${formData.selectedTeamId}#${templateType}`);
          }, 1000);
        },
        'image/png',
        1.0
      );
    } catch (err) {
      showToast('error', 'Failed to generate image. Please try again.');
      setDownloading(false);
      setSavingOverlay('');
    }
  };

  return {
    // State
    teams,
    setTeams,
    downloading,
    saving,
    templateType,
    template,
    setTemplate,
    currentGraphicId,
    setCurrentGraphicId,
    saveMessage,
    savingOverlay,
    backgroundStyle,
    graphicsLimitInfo,
    setGraphicsLimitInfo,
    homeTeamLogo,
    setHomeTeamLogo,
    awayTeamLogoPreview,
    setAwayTeamLogoPreview,
    backgroundPhotoPreview,
    setBackgroundPhotoPreview,
    successMessage,
    toastMessage,
    roster,
    showRosterDropdown,
    setShowRosterDropdown,
    filteredRoster,
    setFilteredRoster,
    playerPhotoPreview,
    originalPlayerPhoto,
    setOriginalPlayerPhoto,
    cutoutPlayerPhoto,
    activePlayerPhotoVersion,
    setCutoutPlayerPhoto,
    setActivePlayerPhotoVersion,
    backgroundCropping,
    backgroundTempImage,
    playerCropping,
    playerTempImage,
    formData,
    setFormData,
    teamColors,
    setTeamColors,
    teamSport,
    setTeamSport,
    isOrgTeam,
    setIsOrgTeam,
    orgTemplateSettings,
    setOrgTemplateSettings,
    showSwitchWarning,
    pendingTeamSwitch,

    // Handlers
    showToast,
    canCreateMore,
    fetchRosterAsync,
    handleTeamChange,
    handleConfirmSwitch,
    handleCancelSwitch,
    handlePlayerPhotoChange,
    handleRemovePlayerPhoto,
    handleChange,
    handleAdditionalInfoChange,
    handleHighlightStatsChange,
    handleTemplateChange,
    handleSave,
    handleSaveAndCreateAnother,
    handleBackgroundStyleChange,
    handleAwayTeamLogoChange,
    handleRemoveAwayTeamLogo,
    handleBackgroundPhotoChange,
    handleRemoveBackgroundPhoto,
    handleBackgroundCropComplete,
    handleBackgroundCropCancel,
    handlePlayerCropComplete,
    handlePlayerCropCancel,
    handleEditBackgroundCrop,
    handleSetBackgroundPhotoFromUrl,
    handleEditPlayerCrop,
    dominantImagePreview,
    originalDominantImage,
    setOriginalDominantImage,
    cutoutDominantImage,
    setCutoutDominantImage,
    activeDominantImageVersion,
    setActiveDominantImageVersion,
    dominantImageCropping,
    dominantImageTempImage,
    handleDominantImageChange,
    handleRemoveDominantImage,
    handleDominantImageCropComplete,
    handleDominantImageCropCancel,
    handleEditDominantImageCrop,
    handlePlayerSearch,
    handlePlayerSelect,
    formatPlayerDisplay,
    handleDownload,

    // Layout customization
    showLayoutModal,
    setShowLayoutModal,
    layoutSettings,
    setLayoutSettings,
    handleLayoutChange: (newSettings) => setLayoutSettings(newSettings),

    // Date format
    showDateFormatModal,
    setShowDateFormatModal,
    dateFormat,
    setDateFormat,
  };
}
